import {
  getCallsStatus,
  type GetCallsStatusReturnType,
  getCapabilities,
} from '@wagmi/core/experimental'
import { config, lingoMainnet } from '@/composables/reown.ts'
import { env } from '@/env.ts'
import { waitForTransactionReceipt } from '@wagmi/core'
import { useStatus } from '@/composables/contracts/status.ts'
import api from '@/services/api.ts'
import { useAppKitAccount } from '@reown/appkit/vue'
import { normalizeEip155ChainId } from '@/composables/wallet/chain-id.ts'

export type Transaction = {
  type: 'calls'
  id: string
} | {
  type: 'transaction'
  hash: `0x${string}`
}

type SendCallsResult = string | { id: string }

export const getCallBundleId = (result: SendCallsResult) => {
  return typeof result === 'string' ? result : result.id
}

export const useTransaction = () => {
  const { updateStatus } = useStatus()
  const appKitAccount = useAppKitAccount()
  const callsStatusTimeoutMs = 60_000

  async function supportsPaymaster(): Promise<boolean> {
    if (appKitAccount.value.embeddedWalletInfo) {
      return false
    }

    try {
      const chainId = normalizeEip155ChainId(lingoMainnet.id)
      if (!chainId) {
        throw new Error(`Invalid chain id: ${String(lingoMainnet.id)}`)
      }

      const capabilities = await getCapabilities(config)
      return Boolean(capabilities[chainId]?.paymasterService?.supported)
    } catch (e) {
      console.error(e)
      return false
    }
  }

  const checkCallStatus = (callStatus: GetCallsStatusReturnType) => {
    const status = String(callStatus.status).toLowerCase()
    const isConfirmed = status === 'confirmed' || status === 'success'
    const someReverted = callStatus.receipts?.some(r => String(r.status).toLowerCase() === 'reverted')
    if (isConfirmed && someReverted) {
      /* TODO: decode logs and throw error with the decoded data */
      throw new Error('Call reverted')
    }
  }

  async function waitForTransaction({ notifyBackend, transaction }: { transaction: Transaction, notifyBackend: boolean }): Promise<string[]> {
    await updateStatus('waitForTransaction', 'global', { loading: true })
    if (transaction.type === 'transaction') {
      await waitForTransactionReceipt(config, { hash: transaction.hash })
      if (notifyBackend) {
        await api.checkTransactions([transaction.hash])
      }
      await updateStatus('waitForTransaction', 'global', { success: true })
      return [transaction.hash]
    }
    const startedAt = Date.now()
    let lastError: unknown = null
    while (true) {
      try {
        const callStatus = await getCallsStatus(config, {
          id: transaction.id,
        })
        const status = String(callStatus.status).toLowerCase()
        if (status === 'confirmed' || status === 'success') {
          checkCallStatus(callStatus)
          const txHashes = callStatus.receipts?.map(r => r.transactionHash) ?? []
          if (notifyBackend && txHashes.length > 0) {
            await api.checkTransactions(txHashes)
          }
          await updateStatus('waitForTransaction', 'global', { success: true })
          return callStatus.receipts?.map(r => r.transactionHash) ?? []
        }
      } catch (e) {
        /* NOTE: This should stop the loop */
        if (e instanceof Error && e.message === 'Call reverted') {
          await updateStatus('waitForTransaction', 'global', { success: false, error: true, loading: false })
          throw e
        }
        lastError = e
      }

      if (Date.now() - startedAt >= callsStatusTimeoutMs) {
        await updateStatus('waitForTransaction', 'global', { success: false, error: true, loading: false })
        throw lastError instanceof Error
          ? lastError
          : new Error(`Timed out while waiting for call bundle ${transaction.id}`)
      }
      await new Promise(resolve => setTimeout(resolve, 1000))
    }
  }

  const paymasterCapabilities = {
    paymasterService: {
      url: `https://paymaster-api.reown.com/${normalizeEip155ChainId(lingoMainnet.id) ?? lingoMainnet.id}/rpc?projectId=${env.reown.projectId}`,
    },
  }

  return { supportsPaymaster, paymasterCapabilities, waitForTransaction }
}
